import { Dialog, Portal } from '@chakra-ui/react';
import { IconButton } from '@components/ui/atoms/IconButton';
import { CloseIcon, Icon } from '@components/ui/atoms/Icons';
import { Text } from '@components/ui/atoms/Text';
import type { ComponentProps, ReactNode } from 'react';
import React from 'react';

type DialogContentProps = ComponentProps<typeof Dialog.Content>;
export type DialogModalProps = {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  maxWidth?: DialogContentProps['maxW'];
  maxHeight?: DialogContentProps['maxH'];
  hideHeader?: boolean;
  closeOnInteractOutside?: boolean;
  children: ReactNode;
  footerContent?: ReactNode;
};

export const DialogModal: React.FC<DialogModalProps> = ({
  isOpen,
  onClose,
  title,
  maxWidth = '600px',
  maxHeight,
  hideHeader = false,
  closeOnInteractOutside = true,
  children,
  footerContent,
}) => {
  return (
    <Portal>
      <Dialog.Root
        open={isOpen}
        onOpenChange={(details) => {
          if (!details.open) {
            onClose();
          }
        }}
        placement="center"
        closeOnInteractOutside={closeOnInteractOutside}
        initialFocusEl={() => null}
      >
        <Dialog.Backdrop bg="black/80" />
        <Dialog.Positioner>
          <Dialog.Content
            maxW={maxWidth}
            maxH={maxHeight}
            w="full"
            bg="body.50"
            borderRadius="xl"
            p={0}
          >
            <Dialog.Header
              hidden={hideHeader}
              p={4}
              borderBottomWidth="1px"
              borderColor="blackAlpha.100"
              borderTopRadius="xl"
              bg="gray.100"
            >
              <Dialog.Title>
                <Text fontSize="lg" fontWeight="600" color="title.dark">
                  {title}
                </Text>
              </Dialog.Title>
              <IconButton
                aria-label="close-dialog"
                position="absolute"
                top={4}
                right={4}
                variant="ghost"
                _hover={{ bg: 'transparent' }}
                onClick={onClose}
              >
                <Icon css={{ width: '18px', height: '18px' }}>
                  <CloseIcon />
                </Icon>
              </IconButton>
            </Dialog.Header>

            <Dialog.Body px={6} py={6} overflow="visible">
              {children}
            </Dialog.Body>

            {footerContent && (
              <Dialog.Footer
                p={4}
                bg="gray.100"
                borderBottomRadius="xl"
                borderTopWidth="1px"
                borderColor="blackAlpha.100"
              >
                {footerContent}
              </Dialog.Footer>
            )}
          </Dialog.Content>
        </Dialog.Positioner>
      </Dialog.Root>
    </Portal>
  );
};

DialogModal.displayName = 'DialogModal';
